import React, { useEffect, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// DateRangePicker — a self-contained reporting-period picker styled to match
// the Newton payments portal. Mirrors the Rainforest dashboard picker:
//
//   [ 📅  June 2026 ▾ ]
//        ┌─────────────────────────────────────────────┐
//        │  Day   Week   Month   Year   Custom          │
//        │  «      2026      »      [ This Month ]       │
//        │  JAN  FEB  MAR  APR     [ Last Month ]        │
//        │  MAY  JUN  JUL  AUG                           │
//        │  SEP  OCT  NOV  DEC                           │
//        └─────────────────────────────────────────────┘
//
// Works purely in local time and speaks the same ``YYYY-MM-DD`` string
// contract the rest of ReportTab uses. Calls ``onChange(startStr, endStr)``
// whenever a complete range is chosen.
// ---------------------------------------------------------------------------

const MONTHS_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const ACCENT = '#1f5a3d';

const toInputDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Parse a ``YYYY-MM-DD`` string into a *local* midnight Date (avoids the UTC
// shift that ``new Date('2026-06-01')`` introduces).
const parseInputDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const startOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay()); // Sunday
  return d;
};
const endOfWeek = (date) => {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  return d;
};
const monthRange = (year, month) => ({
  start: new Date(year, month, 1),
  end: new Date(year, month + 1, 0),
});
const yearRange = (year) => ({
  start: new Date(year, 0, 1),
  end: new Date(year, 11, 31),
});

// Smart label for the trigger button — collapses clean ranges into a tidy
// phrase ("June 2026", "2026", single day), else shows the span.
const formatRangeLabel = (startStr, endStr) => {
  const start = parseInputDate(startStr);
  const end = parseInputDate(endStr);
  if (!start || !end) return 'Select dates';

  if (sameDay(start, end)) {
    return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const isFirstOfMonth = start.getDate() === 1;
  const isLastOfMonth = end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();

  if (isFirstOfMonth && isLastOfMonth && sameMonth) {
    return `${MONTHS_LONG[start.getMonth()]} ${start.getFullYear()}`;
  }
  if (isFirstOfMonth && isLastOfMonth && start.getMonth() === 0 && end.getMonth() === 11 && sameYear) {
    return `${start.getFullYear()}`;
  }

  const startFmt = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endFmt = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startFmt} – ${endFmt}`;
};

const detectMode = (startStr, endStr) => {
  const start = parseInputDate(startStr);
  const end = parseInputDate(endStr);
  if (!start || !end) return 'custom';
  if (sameDay(start, end)) return 'day';
  if (sameDay(start, startOfWeek(start)) && sameDay(end, endOfWeek(start))) return 'week';
  const lastOfMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0);
  if (start.getDate() === 1 && sameDay(end, lastOfMonth) && start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return 'month';
  }
  if (start.getMonth() === 0 && start.getDate() === 1 && end.getMonth() === 11 && end.getDate() === 31 && start.getFullYear() === end.getFullYear()) {
    return 'year';
  }
  return 'custom';
};

// Scoped stylesheet — gives us real :hover and transitions (impossible with
// inline styles) without leaking global classes. Injected once.
const STYLE_ID = 'newton-drp-styles';
const STYLES = `
.drp-pop { --accent: ${ACCENT}; }
.drp-tab { flex:1; padding:9px 4px; font-size:0.82rem; font-weight:600; text-align:center; cursor:pointer; color:#6b716d; border-bottom:2px solid transparent; transition:color .15s, border-color .15s; user-select:none; }
.drp-tab:hover { color:var(--accent); }
.drp-tab.active { color:var(--accent); border-bottom-color:var(--accent); }
.drp-nav { border:none; background:transparent; cursor:pointer; font-size:1rem; line-height:1; color:#3c4540; width:28px; height:28px; border-radius:6px; display:inline-flex; align-items:center; justify-content:center; transition:background .12s; }
.drp-nav:hover { background:#eef0ee; }
.drp-dow { text-align:center; font-size:0.68rem; font-weight:700; color:#a3a8a4; padding-bottom:4px; }
.drp-day { height:36px; display:flex; align-items:center; justify-content:center; cursor:pointer; position:relative; }
.drp-day-num { width:30px; height:30px; display:flex; align-items:center; justify-content:center; border-radius:50%; font-size:0.8rem; font-weight:500; color:#1a1d1b; transition:background .12s, color .12s; position:relative; z-index:1; }
.drp-day:hover .drp-day-num { background:#f2f6f3; }
.drp-day.selected .drp-day-num { background:var(--accent); color:#fff; font-weight:700; }
.drp-day.today .drp-day-num { box-shadow: inset 0 0 0 1.5px var(--accent); color:var(--accent); font-weight:700; }
.drp-day.today.selected .drp-day-num { color:#fff; }
.drp-band { position:absolute; top:3px; bottom:3px; left:0; right:0; background:#e6efe9; z-index:0; }
.drp-band.hover { background:#f3f7fb; }
.drp-band.l { left:3px; border-top-left-radius:18px; border-bottom-left-radius:18px; }
.drp-band.r { right:3px; border-top-right-radius:18px; border-bottom-right-radius:18px; }
.drp-cell { padding:11px 0; border-radius:8px; text-align:center; font-size:0.82rem; font-weight:600; cursor:pointer; color:#1a1d1b; transition:background .12s, color .12s; user-select:none; }
.drp-cell:hover { background:#f2f6f3; color:var(--accent); }
.drp-cell.active { background:var(--accent); color:#fff; }
.drp-cell.active:hover { background:#174a31; color:#fff; }
.drp-quick { display:block; width:100%; padding:9px 12px; margin-bottom:8px; border:1px solid var(--accent); border-radius:8px; background:#fff; color:var(--accent); font-size:0.82rem; font-weight:700; cursor:pointer; white-space:nowrap; transition:background .12s; }
.drp-quick:hover { background:#e6efe9; }
.drp-trigger { display:inline-flex; align-items:center; gap:8px; padding:8px 12px; border:1px solid #d6dbd7; border-radius:8px; background:#fff; font-size:0.9rem; font-weight:600; color:#1a1d1b; cursor:pointer; min-width:180px; justify-content:space-between; transition:border-color .12s, box-shadow .12s; }
.drp-trigger:hover { border-color:#8a908c; }
.drp-trigger.open { border-color:var(--accent); box-shadow:0 0 0 3px rgba(25,118,210,0.12); }
.drp-done { align-self:flex-end; padding:8px 18px; border:none; border-radius:8px; background:var(--accent); color:#fff; font-size:0.85rem; font-weight:700; cursor:pointer; transition:background .12s; }
.drp-done:hover { background:#174a31; }
`;

const useInjectStyles = () => {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
  }, []);
};

const DateRangePicker = ({ startDate, endDate, onChange }) => {
  useInjectStyles();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(() => detectMode(startDate, endDate));
  const initial = parseInputDate(endDate) || parseInputDate(startDate) || new Date();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  // Decade base for the Year grid.
  const [yearGridBase, setYearGridBase] = useState(initial.getFullYear() - (initial.getFullYear() % 10));
  // Hovered day (week-mode preview band).
  const [hoverDate, setHoverDate] = useState(null);

  const containerRef = useRef(null);

  const today = new Date();
  const selStart = parseInputDate(startDate);
  const selEnd = parseInputDate(endDate);

  // Re-sync internal mode/view when the popover opens (in case the value
  // changed externally).
  useEffect(() => {
    if (open) {
      setMode(detectMode(startDate, endDate));
      const base = parseInputDate(endDate) || parseInputDate(startDate) || new Date();
      setViewYear(base.getFullYear());
      setViewMonth(base.getMonth());
      setYearGridBase(base.getFullYear() - (base.getFullYear() % 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const apply = (start, end, { close = true } = {}) => {
    onChange(toInputDate(start), toInputDate(end));
    if (close) setOpen(false);
  };

  const label = useMemo(() => formatRangeLabel(startDate, endDate), [startDate, endDate]);

  const navBtnRow = (text, onPrev, onNext) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
      <button type="button" className="drp-nav" onClick={onPrev}>‹</button>
      <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1a1d1b' }}>{text}</span>
      <button type="button" className="drp-nav" onClick={onNext}>›</button>
    </div>
  );

  // --- day / week calendar -------------------------------------------------
  // ``selectionType`` is 'day' or 'week' and controls how cells highlight.
  const renderDayCalendar = (selectionType, onPickDate) => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const startPad = firstOfMonth.getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(viewYear, viewMonth, d));

    const gotoPrev = () => {
      if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); } else setViewMonth((m) => m - 1);
    };
    const gotoNext = () => {
      if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); } else setViewMonth((m) => m + 1);
    };

    const selWeekStart = selectionType === 'week' && selStart ? startOfWeek(selStart) : null;
    const selWeekEnd = selectionType === 'week' && selStart ? endOfWeek(selStart) : null;
    const hoverWeekStart = selectionType === 'week' && hoverDate ? startOfWeek(hoverDate) : null;
    const hoverWeekEnd = selectionType === 'week' && hoverDate ? endOfWeek(hoverDate) : null;

    const inRange = (date, a, b) => a && b && date >= a && date <= b;

    return (
      <div style={{ width: '252px' }}>
        {navBtnRow(`${MONTHS_LONG[viewMonth]} ${viewYear}`, gotoPrev, gotoNext)}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {WEEKDAYS.map((w) => (<div key={w} className="drp-dow">{w}</div>))}
        </div>
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}
          onMouseLeave={() => setHoverDate(null)}
        >
          {cells.map((date, idx) => {
            if (!date) return <div key={`pad-${idx}`} />;

            const isSelectedDay = selectionType === 'day' && sameDay(date, selStart) && sameDay(date, selEnd);
            const isToday = sameDay(date, today);

            const inSelWeek = inRange(date, selWeekStart, selWeekEnd);
            const inHoverWeek = inRange(date, hoverWeekStart, hoverWeekEnd);
            const dow = date.getDay();
            const isMonthFirst = date.getDate() === 1;
            const isMonthLast = date.getDate() === daysInMonth;
            const leftEnd = dow === 0 || isMonthFirst;
            const rightEnd = dow === 6 || isMonthLast;

            const classes = ['drp-day'];
            if (isSelectedDay) classes.push('selected');
            if (isToday) classes.push('today');

            return (
              <div
                key={toInputDate(date)}
                className={classes.join(' ')}
                role="button"
                tabIndex={0}
                onClick={() => onPickDate(date)}
                onMouseEnter={() => selectionType === 'week' && setHoverDate(date)}
              >
                {(inSelWeek || inHoverWeek) && (
                  <span
                    className={[
                      'drp-band',
                      inSelWeek ? '' : 'hover',
                      leftEnd ? 'l' : '',
                      rightEnd ? 'r' : '',
                    ].join(' ')}
                  />
                )}
                <span className="drp-day-num">{date.getDate()}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (mode === 'day') {
      return (
        <div style={{ display: 'flex', gap: '16px' }}>
          {renderDayCalendar('day', (date) => apply(date, date))}
          <div style={{ width: '120px' }}>
            <button type="button" className="drp-quick" onClick={() => { const t = new Date(); apply(t, t); }}>
              Today
            </button>
            <button
              type="button"
              className="drp-quick"
              onClick={() => { const t = new Date(); t.setDate(t.getDate() - 1); apply(t, t); }}
            >
              Yesterday
            </button>
          </div>
        </div>
      );
    }

    if (mode === 'week') {
      return (
        <div style={{ display: 'flex', gap: '16px' }}>
          {renderDayCalendar('week', (date) => apply(startOfWeek(date), endOfWeek(date)))}
          <div style={{ width: '120px' }}>
            <button
              type="button"
              className="drp-quick"
              onClick={() => { const t = new Date(); apply(startOfWeek(t), endOfWeek(t)); }}
            >
              This Week
            </button>
            <button
              type="button"
              className="drp-quick"
              onClick={() => { const t = new Date(); t.setDate(t.getDate() - 7); apply(startOfWeek(t), endOfWeek(t)); }}
            >
              Last Week
            </button>
          </div>
        </div>
      );
    }

    if (mode === 'month') {
      const isActiveMonth = (m) => {
        if (!selStart || !selEnd) return false;
        const r = monthRange(viewYear, m);
        return sameDay(r.start, selStart) && sameDay(r.end, selEnd);
      };
      return (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ width: '252px' }}>
            {navBtnRow(viewYear, () => setViewYear((y) => y - 1), () => setViewYear((y) => y + 1))}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
              {MONTHS_SHORT.map((mLabel, m) => (
                <div
                  key={mLabel}
                  role="button"
                  tabIndex={0}
                  onClick={() => { const r = monthRange(viewYear, m); apply(r.start, r.end); }}
                  className={`drp-cell${isActiveMonth(m) ? ' active' : ''}`}
                >
                  {mLabel}
                </div>
              ))}
            </div>
          </div>
          <div style={{ width: '120px' }}>
            <button
              type="button"
              className="drp-quick"
              onClick={() => { const t = new Date(); const r = monthRange(t.getFullYear(), t.getMonth()); apply(r.start, r.end); }}
            >
              This Month
            </button>
            <button
              type="button"
              className="drp-quick"
              onClick={() => { const t = new Date(); const r = monthRange(t.getFullYear(), t.getMonth() - 1); apply(r.start, r.end); }}
            >
              Last Month
            </button>
          </div>
        </div>
      );
    }

    if (mode === 'year') {
      const years = Array.from({ length: 12 }, (_, i) => yearGridBase - 1 + i);
      const isActiveYear = (y) => {
        if (!selStart || !selEnd) return false;
        const r = yearRange(y);
        return sameDay(r.start, selStart) && sameDay(r.end, selEnd);
      };
      return (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ width: '252px' }}>
            {navBtnRow(`${yearGridBase}–${yearGridBase + 9}`, () => setYearGridBase((b) => b - 10), () => setYearGridBase((b) => b + 10))}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
              {years.map((y) => (
                <div
                  key={y}
                  role="button"
                  tabIndex={0}
                  onClick={() => { const r = yearRange(y); apply(r.start, r.end); }}
                  className={`drp-cell${isActiveYear(y) ? ' active' : ''}`}
                >
                  {y}
                </div>
              ))}
            </div>
          </div>
          <div style={{ width: '120px' }}>
            <button
              type="button"
              className="drp-quick"
              onClick={() => { const r = yearRange(new Date().getFullYear()); apply(r.start, r.end); }}
            >
              This Year
            </button>
            <button
              type="button"
              className="drp-quick"
              onClick={() => { const r = yearRange(new Date().getFullYear() - 1); apply(r.start, r.end); }}
            >
              Last Year
            </button>
          </div>
        </div>
      );
    }

    // Custom
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            <label style={{ fontSize: '0.72rem', color: '#6b716d' }}>From</label>
            <input
              type="date"
              value={startDate || ''}
              max={endDate || undefined}
              onChange={(e) => onChange(e.target.value, endDate)}
              style={{ padding: '8px 10px', border: '1px solid #d6dbd7', borderRadius: '6px', fontSize: '0.9rem' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            <label style={{ fontSize: '0.72rem', color: '#6b716d' }}>To</label>
            <input
              type="date"
              value={endDate || ''}
              min={startDate || undefined}
              onChange={(e) => onChange(startDate, e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #d6dbd7', borderRadius: '6px', fontSize: '0.9rem' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="drp-quick"
            style={{ marginBottom: 0, flex: 1 }}
            onClick={() => { const t = new Date(); apply(t, t); }}
          >
            Today
          </button>
          <button
            type="button"
            className="drp-quick"
            style={{ marginBottom: 0, flex: 1 }}
            onClick={() => { const t = new Date(); apply(new Date(t.getFullYear(), t.getMonth(), 1), t); }}
          >
            This Month
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="drp-quick"
            style={{ marginBottom: 0, flex: 1 }}
            onClick={() => { const t = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); apply(s, t); }}
          >
            Last 7 days
          </button>
          <button
            type="button"
            className="drp-quick"
            style={{ marginBottom: 0, flex: 1 }}
            onClick={() => { const t = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); apply(s, t); }}
          >
            Last 30 days
          </button>
        </div>
        <button type="button" className="drp-done" onClick={() => setOpen(false)}>Done</button>
      </div>
    );
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className={`drp-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span aria-hidden>📅</span>
          {label}
        </span>
        <span style={{ color: '#a3a8a4', fontSize: '0.7rem' }}>▾</span>
      </button>

      {open && (
        <div
          className="drp-pop"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 50,
            background: 'white',
            border: '1px solid #e3e7e4',
            borderRadius: '12px',
            boxShadow: '0 10px 32px rgba(16,24,40,0.18)',
            padding: '16px',
            // Fixed width so the popover dimensions stay identical no matter
            // which tab is active (252px calendar + 16px gap + 120px quick
            // column + 2×16px padding).
            width: '420px',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', borderBottom: '1px solid #eef0ee', marginBottom: '14px' }}>
            {['day', 'week', 'month', 'year', 'custom'].map((m) => (
              <div
                key={m}
                className={`drp-tab${mode === m ? ' active' : ''}`}
                onClick={() => setMode(m)}
              >
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </div>
            ))}
          </div>
          {renderBody()}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
